import { ReactNode, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '@/store';
import { authService } from '@/services/authService';
import { env } from '@/config/env';
import LoadingSpinner from '@/components/UI/LoadingSpinner';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Authentication guard component
 * Protects routes based on VITE_AUTH_ENABLED environment variable
 */
const AuthGuard = ({ children }: AuthGuardProps) => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth);

  // Ref to track if session restoration has been attempted to prevent race conditions
  const sessionRestorationAttempted = useRef(false);

  // If authentication is disabled, allow all access
  if (!env.authEnabled) {
    return <>{children}</>;
  }

  // CRITICAL: Check localStorage directly to avoid race condition
  // This prevents redirect before useEffect has chance to restore session
  const hasLocalStorageSession = authService.isAuthenticated();

  // With redux-persist, user data is automatically rehydrated from localStorage
  // PersistGate handles rehydration automatically on app startup
  // Mark that session restoration is handled by redux-persist
  sessionRestorationAttempted.current = true;

  // Note: Token expiration checking is handled by authService.checkForProactiveRefresh()
  // which runs on a configurable interval. No need for duplicate checking here.

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // CRITICAL FIX: Check BOTH Redux state AND localStorage
  // Prevents race condition where Redux hasn't updated yet but localStorage has session
  const isActuallyAuthenticated = isAuthenticated || hasLocalStorageSession;

  // Redirect to login if not authenticated in either Redux or localStorage
  if (!isActuallyAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // User is authenticated, render protected content
  return <>{children}</>;
};

export default AuthGuard;